import api from './api';

export interface SaasPlan {
  id: string;
  slug: string;
  name: string;
  description?: string;
  billingType: string;
  currencyCode: string;
  monthlyAmountCents?: number;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export type SaasPlanWrite = {
  slug: string;
  name: string;
  description?: string;
  billingType: 'fixed_monthly' | 'pay_as_you_go';
  currencyCode: string;
  monthlyAmountCents?: number;
  isActive: boolean;
  sortOrder: number;
};

export interface AdminRefundRequest {
  id: string;
  tenantId: string;
  tenantName?: string;
  studentAuthUserId: string;
  studentDisplayName?: string;
  subscriptionId?: string;
  productKind: string;
  status: string;
  reason?: string;
  adminNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export const superadminBillingService = {
  async listSaasPlans(activeOnly = false): Promise<SaasPlan[]> {
    const res = await api.get<SaasPlan[]>('/billing/saas-plans', {
      params: { activeOnly },
    });
    return res.data;
  },

  async createSaasPlan(body: SaasPlanWrite): Promise<SaasPlan> {
    const res = await api.post<SaasPlan>('/billing/saas-plans', body);
    return res.data;
  },

  async updateSaasPlan(id: string, body: SaasPlanWrite): Promise<SaasPlan> {
    const res = await api.put<SaasPlan>(`/billing/saas-plans/${id}`, body);
    return res.data;
  },

  async deactivateSaasPlan(id: string): Promise<SaasPlan> {
    const res = await api.post<SaasPlan>(`/billing/saas-plans/${id}/deactivate`);
    return res.data;
  },

  async listRefundRequests(params?: {
    status?: string;
    tenantId?: string;
  }): Promise<AdminRefundRequest[]> {
    const res = await api.get<AdminRefundRequest[]>('/billing/admin/refund-requests', {
      params,
    });
    return res.data;
  },

  async updateRefundRequest(
    id: string,
    body: { status?: string; adminNotes?: string }
  ): Promise<AdminRefundRequest> {
    const res = await api.patch<AdminRefundRequest>(`/billing/admin/refund-requests/${id}`, body);
    return res.data;
  },

  async runPaygUsageReport(): Promise<PaygUsageReportResult> {
    const res = await api.post<PaygUsageReportResult>('/billing/admin/payg-usage-report/run');
    return res.data;
  },

  async listPaymentWebhooks(limit = 30): Promise<PaymentWebhookEventRow[]> {
    const res = await api.get<PaymentWebhookEventRow[]>('/billing/admin/webhooks', {
      params: { limit },
    });
    return res.data;
  },

  async replayPaymentWebhook(id: string): Promise<{ replayed: boolean }> {
    const res = await api.post<{ replayed: boolean }>(
      `/billing/admin/webhooks/${id}/replay`
    );
    return res.data;
  },

  async listBillingStudents(tenantId: string): Promise<BillingStreamStudentOption[]> {
    const res = await api.get<BillingStreamStudentOption[]>(
      `/billing/admin/tenants/${tenantId}/billing-students`
    );
    return res.data;
  },

  async getBillingStream(
    tenantId: string,
    params?: { studentAuthUserId?: string; teacherAuthUserId?: string; limit?: number }
  ): Promise<BillingActivityStreamResult> {
    const res = await api.get<BillingActivityStreamResult>(
      `/billing/admin/tenants/${tenantId}/billing-stream`,
      { params }
    );
    return res.data;
  },
};

export interface BillingStreamStudentOption {
  studentAuthUserId: string;
  displayName: string;
  emailHint?: string;
}

export type BillingStreamItemKind =
  | 'db_change'
  | 'payment_webhook'
  | 'credit_ledger'
  | 'refund_request';

export interface BillingStreamItem {
  id: string;
  kind: BillingStreamItemKind;
  occurredAt: string;
  tenantId?: string;
  studentAuthUserId?: string;
  studentDisplayName?: string;
  teacherAuthUserId?: string;
  teacherDisplayName?: string;
  title: string;
  summary?: string;
  source?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  paymentWebhookEventId?: string;
  paymentEventName?: string;
  signatureValid?: boolean;
  processError?: string;
  stateBefore?: unknown;
  stateAfter?: unknown;
  rawWebhookPayload?: unknown;
}

export interface BillingActivityStreamResult {
  items: BillingStreamItem[];
  totalReturned: number;
}

export interface PaymentWebhookEventRow {
  id: string;
  provider: string;
  eventName: string;
  signatureValid: boolean;
  processedAt?: string;
  processError?: string;
  createdAt: string;
}

export interface PaygUsageReportResult {
  tenantsConsidered: number;
  usageRecordsSent: number;
  skipped: number;
  messages: string[];
}
